import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabase';

interface CreditContextType {
  credits: number;
  loading: boolean;
  error: string | null;
  checkCredits: () => Promise<boolean>;
  useCredits: (amount: number) => Promise<boolean>;
  refreshCredits: () => Promise<void>;
}

const CreditContext = createContext<CreditContextType>({
  credits: 0,
  loading: false,
  error: null,
  checkCredits: async () => false,
  useCredits: async () => false,
  refreshCredits: async () => {},
});

export const useCredits = () => useContext(CreditContext);

export const CreditProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshCredits = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Ensure user records exist first (with error handling)
      try {
        await supabase.rpc('ensure_user_records_exist', {
          p_user_id: user.id
        });
      } catch (setupError) {
        console.warn('Error ensuring user records:', setupError);
        // Continue anyway - the query might still work
      }
      
      const { data, error } = await supabase
        .from('user_credits')
        .select('credits')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setCredits(data?.credits || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch credits');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      refreshCredits();
    } else {
      setCredits(0);
    }
  }, [user]);

  const checkCredits = async (amount = 1): Promise<boolean> => {
    await refreshCredits();
    return credits >= amount;
  };

  const useCredits = async (amount: number): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.rpc('use_credits', {
        p_user_id: user.id,
        p_credit_amount: amount,
      });

      if (error) throw error;
      await refreshCredits();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to use credits');
      return false;
    }
  };

  return (
    <CreditContext.Provider value={{
      credits,
      loading,
      error,
      checkCredits,
      useCredits,
      refreshCredits,
    }}>
      {children}
    </CreditContext.Provider>
  );
};